export type SourceApp =
  | 'fund'
  | 'investment'
  | 'portfolio'
  | 'exit'
  | 'research'
  | 'general';

export type WorkItemStatus =
  | 'notStarted'
  | 'inProgress'
  | 'pendingConfirmation'
  | 'reminder'
  | 'completed'
  | 'cancelled';
export type Priority = 'high' | 'medium' | 'low';
export type ReminderLevel = 'urgent' | 'important' | 'normal' | 'info';
export type ReminderStatus = 'unread' | 'read' | 'snoozed' | 'ignored';
export type RoleView =
  | 'investmentManager'
  | 'partner'
  | 'fundraising'
  | 'portfolio'
  | 'exit'
  | 'research';
export type FocusType =
  | 'project'
  | 'company'
  | 'fund'
  | 'lp'
  | 'exit'
  | 'research'
  | 'resource';

export interface SourceEntity {
  id: string;
  sourceApp: SourceApp;
  objectType: FocusType;
  name: string;
  subtitle: string;
  industry: string;
  stage: string;
  owner: string;
  riskLevel: Priority | 'none';
  riskHandled?: boolean;
  latestChange: string;
  nextNode: string;
  nextNodeDate: string;
  amount?: number;
  location?: string;
  tags: string[];
  summary: string;
}

export interface WorkItem {
  id: string;
  title: string;
  sourceApp: SourceApp;
  entityId?: string;
  objectType?: string;
  relatedObject: string;
  type: string;
  priority: Priority;
  owner: string;
  initiator: string;
  /** ISO/本地时间；缺失表示无期限，禁止前端伪造到期日。 */
  dueAt?: string | null;
  status: WorkItemStatus;
  nextAction: string;
  general: boolean;
  /** bff=跨域业务提醒（只读）；task=任务中心可办理；local=本地演示项。 */
  origin?: 'bff' | 'task' | 'local';
}

export interface ScheduleEvent {
  id: string;
  title: string;
  sourceApp: SourceApp;
  entityId?: string;
  date: string;
  startTime: string;
  endTime: string;
  type: string;
  location: string;
  participants: string[];
  agenda: string[];
  personal: boolean;
  completed?: boolean;
  relatedTaskIds: string[];
}

export interface FocusObject {
  id: string;
  entityId: string;
  type: FocusType;
  level: Priority;
  followedAt: string;
}

export interface Activity {
  id: string;
  title: string;
  description: string;
  sourceApp: SourceApp;
  entityId?: string;
  objectName: string;
  occurredAt: string;
  category: 'project' | 'market' | 'internal' | 'system';
  read: boolean;
  tags: string[];
}

export interface Reminder {
  id: string;
  title: string;
  description: string;
  suggestion: string;
  sourceApp: SourceApp;
  entityId?: string;
  level: ReminderLevel;
  status: ReminderStatus;
  occurredAt: string;
}

export interface DashboardPreference {
  roleView: RoleView;
  moduleOrder: string[];
  hiddenModules: string[];
  reminderPreferences: Record<string, boolean>;
  homeScope: 'mine' | 'team' | 'organization';
}

export interface ViewState {
  workItemTab: string;
  activityTab: string;
  focusTab: string;
  scheduleView: 'day' | 'week' | 'month' | 'list';
  filters: Record<string, string | string[]>;
  scrollPositions: Record<string, number>;
}

export interface WorkbenchState {
  schemaVersion: 1;
  generatedAt: string;
  sourceEntities: SourceEntity[];
  workItems: WorkItem[];
  schedules: ScheduleEvent[];
  focusObjects: FocusObject[];
  activities: Activity[];
  reminders: Reminder[];
  preference: DashboardPreference;
  viewState: ViewState;
}

export interface DashboardMetrics {
  todayTasks: number;
  overdueTasks: number;
  todayMeetings: number;
  pendingDecisions: number;
  highRisks: number;
  focusedObjects: number;
}

export type WorkbenchAction =
  | { type: 'workItem/complete'; payload: { id: string } }
  | { type: 'workItem/postpone'; payload: { id: string; dueAt?: string } }
  | { type: 'workItem/reassign'; payload: { ids: string[]; owner: string } }
  | { type: 'workItem/create'; payload: WorkItem }
  | { type: 'schedule/create'; payload: ScheduleEvent }
  | { type: 'schedule/update'; payload: ScheduleEvent }
  | { type: 'schedule/complete'; payload: { id: string } }
  | { type: 'focus/toggle'; payload: { entityId: string } }
  | { type: 'reminder/read'; payload: { id: string } }
  | { type: 'reminder/snooze'; payload: { id: string } }
  | { type: 'reminder/ignore'; payload: { id: string } }
  | { type: 'reminder/createTask'; payload: { id: string } }
  | { type: 'source/handleRisk'; payload: { entityId: string } }
  | { type: 'sourceEntities/set'; payload: SourceEntity[] }
  | { type: 'preference/update'; payload: Partial<DashboardPreference> }
  | { type: 'view/update'; payload: Partial<ViewState> }
  | { type: 'state/import'; payload: WorkbenchState }
  | { type: 'state/reset'; payload: WorkbenchState };

export const SOURCE_APP_LABELS: Record<SourceApp, string> = {
  fund: '基金与募资',
  investment: '投资项目',
  portfolio: '投后管理',
  exit: '退出管理',
  research: '投资研究与资源',
  general: '通用待办',
};

export const ROLE_VIEW_LABELS: Record<RoleView, string> = {
  investmentManager: '投资经理视图',
  partner: '合伙人视图',
  fundraising: '募资视图',
  portfolio: '投后视图',
  exit: '退出视图',
  research: '研究视图',
};
